import React from 'react';
import { Box, Typography, Button, TextField, ToggleButton, ToggleButtonGroup, CircularProgress } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import type { Bill, BillLineItem, POLineDetail, PurchaseOrder, Supplier } from '../data/types';
import { BRAND, BRAND_LIGHT, FI_ORANGE, INK_MUTE } from '../theme/tokens';
import { TableCellInput } from './TableCellInput';
import {
  buildReconcileBands,
  computeSideTotals,
  fmtMoney,
  mulGst,
  poLineEx,
  type MatchedPair,
} from '../utils/reconcileDifferenceView';

// Band colours — reconciliation scan palette (distinct from brand purple)
const GO = { main: '#0f9d63', ink: '#0a6e45', bg: '#e7f6ef', line: '#cdebdc' };
const WARN = { main: '#e65100', ink: '#bf360c', bg: '#fff3e0', line: '#ffcc80' };
const SLATE = { main: '#3f6ad8', ink: '#2b4fa6', bg: '#eaf0fd', line: '#cbdaf6' };

// Side identity — purple = Purchase Order (ordered), orange = Supplier Bill
// (invoiced). Carried through header, row tint and footer so the two sides
// read as distinct at a glance.
const PO_SIDE = { accent: BRAND, headerBg: '#f1ebff', rowBg: '#fbfaff', label: 'Purchase Order' };
const BILL_SIDE = { accent: FI_ORANGE, headerBg: '#fff1e3', rowBg: '#fffaf4', label: 'Supplier Bill' };

const SPINE_W = 96;

export interface BillReconcileSideBySideProps {
  linkedPo: PurchaseOrder;
  bill: Bill;
  billImported: boolean;
  /** True while Alfred / Claude is parsing the invoice PDF */
  processing?: boolean;
  lineItems: BillLineItem[];
  poLinesForRecon: POLineDetail[];
  billMarkup: string;
  onBillMarkupChange: (v: string) => void;
  gstMode: 'ex' | 'inc';
  onGstModeChange: (v: 'ex' | 'inc') => void;
  acceptedExtras: Set<number>;
  /** Add a bill-only line onto the PO immediately */
  onAcceptExtra: (billLineId: number) => void;
  onAcceptAllExtras: () => void;
  /** Edit PO Qty / Received Qty (FI Jobs & inventory — no Actual column) */
  onUpdatePoLineField?: (
    inventoryCode: string,
    field: 'orderedQty' | 'receivedToDate',
    value: number,
  ) => void;
  /** One-click: set Received = bill qty on every matched PO line */
  onReceiveAllMatched?: () => void;
  /** Edit bill invoiced qty (right column) */
  onUpdateBillQty?: (billLineId: number, qty: number) => void;
  supplier?: Supplier | null;
}

// ── Qty editor ───────────────────────────────────────────────────────────────

const QtyEdit: React.FC<{
  value: number;
  onSave?: (v: number) => void;
  accent?: string;
  ariaLabel: string;
  width?: number;
  suffix?: React.ReactNode;
}> = ({ value, onSave, accent = '#555', ariaLabel, width = 40, suffix }) => {
  if (!onSave) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.3 }}>
        <Typography sx={{ textAlign: 'right', color: INK_MUTE, fontSize: '0.78rem', fontVariantNumeric: 'tabular-nums' }}>
          {value}
        </Typography>
        {suffix}
      </Box>
    );
  }
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.3 }}>
      <TableCellInput
        value={String(value)}
        numeric
        align="right"
        width={width}
        fontSize="0.74rem"
        fontWeight={700}
        color={accent}
        aria-label={ariaLabel}
        onCommit={(raw) => {
          const parsed = parseFloat(raw);
          if (isNaN(parsed) || parsed < 0) return;
          if (parsed !== value) onSave(parsed);
        }}
      />
      {suffix}
    </Box>
  );
};

/** Item · Qty · Rcvd · Unit · Line — unit+line for side-by-side price compare. */
const PO_QTY_GRID = 'minmax(0,1.4fr) 40px 52px 68px 76px';
/** Item · Bill Qty · Unit · Line */
const BILL_QTY_GRID = 'minmax(0,1.4fr) 52px 68px 76px';

// ── PO cell — Qty / Received / Unit / Line ───────────────────────────────────

const PoItemCell: React.FC<{
  code: string;
  desc: string;
  orderedQty: number;
  receivedQty: number;
  unitPrice: string;
  amount: string;
  backgroundColor?: string;
  onUpdate?: (field: 'orderedQty' | 'receivedToDate', qty: number) => void;
}> = ({ code, desc, orderedQty, receivedQty, unitPrice, amount, backgroundColor, onUpdate }) => {
  const fullyReceived = receivedQty >= orderedQty && orderedQty > 0;
  const partiallyReceived = receivedQty > 0 && !fullyReceived;
  const rcvdColor = fullyReceived ? '#2e7d32' : partiallyReceived ? FI_ORANGE : '#888';
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: PO_QTY_GRID,
        gap: 0.5,
        alignItems: 'center',
        px: 1.2, py: 1.2,
        borderBottom: '1px solid #f0eef6',
        backgroundColor: backgroundColor ?? PO_SIDE.rowBg,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: BRAND, lineHeight: 1.2 }}>
          {code || '—'}
        </Typography>
        <Typography sx={{ fontSize: '0.74rem', color: '#3a3550', mt: 0.15 }} noWrap>{desc}</Typography>
      </Box>
      <QtyEdit
        value={orderedQty}
        onSave={onUpdate ? (q) => onUpdate('orderedQty', q) : undefined}
        accent={BRAND}
        ariaLabel="Qty (ordered)"
        width={34}
      />
      <QtyEdit
        value={receivedQty}
        onSave={onUpdate ? (q) => onUpdate('receivedToDate', q) : undefined}
        accent={rcvdColor}
        ariaLabel="Received Qty"
        width={34}
        suffix={
          <Typography sx={{ fontSize: '0.6rem', color: '#aaa', whiteSpace: 'nowrap' }}>
            /{orderedQty}
          </Typography>
        }
      />
      <Typography sx={{ textAlign: 'right', fontWeight: 600, fontSize: '0.74rem', fontVariantNumeric: 'tabular-nums', color: '#3a3550' }}>{unitPrice}</Typography>
      <Typography sx={{ textAlign: 'right', fontWeight: 700, fontSize: '0.78rem', fontVariantNumeric: 'tabular-nums' }}>{amount}</Typography>
    </Box>
  );
};

// ── Bill cell — Bill Qty · Unit · Line ───────────────────────────────────────

const BillItemCell: React.FC<{
  code: string;
  desc: string;
  sup?: string;
  qty: number;
  unitPrice: string;
  amount: string;
  backgroundColor?: string;
  onQtyChange?: (qty: number) => void;
}> = ({ code, desc, sup, qty, unitPrice, amount, backgroundColor, onQtyChange }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: BILL_QTY_GRID,
      gap: 0.6,
      alignItems: 'center',
      px: 1.5, py: 1.3,
      borderBottom: '1px solid #f0eef6',
      backgroundColor: backgroundColor ?? BILL_SIDE.rowBg,
    }}
  >
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#1b1726', lineHeight: 1.2 }}>
        {code || '—'}
      </Typography>
      <Typography sx={{ fontSize: '0.74rem', color: '#3a3550', mt: 0.15 }} noWrap>{desc}</Typography>
      {sup && (
        <Typography sx={{ fontSize: '0.64rem', color: '#9b96ac', mt: 0.15 }}>Supplier {sup}</Typography>
      )}
    </Box>
    <QtyEdit
      value={qty}
      onSave={onQtyChange}
      accent={FI_ORANGE}
      ariaLabel="Bill qty"
      width={40}
    />
    <Typography sx={{ textAlign: 'right', fontWeight: 600, fontSize: '0.74rem', fontVariantNumeric: 'tabular-nums', color: '#3a3550' }}>{unitPrice}</Typography>
    <Typography sx={{ textAlign: 'right', fontWeight: 700, fontSize: '0.78rem', fontVariantNumeric: 'tabular-nums' }}>{amount}</Typography>
  </Box>
);

const GhostCell: React.FC<{ label: string; tone?: 'default' | 'outstanding' | 'extra' }> = ({ label, tone = 'default' }) => (
  <Box
    sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      px: 2, py: 1.4, borderBottom: '1px solid #f0eef6',
      background: tone === 'outstanding'
        ? 'repeating-linear-gradient(135deg, #fff3e8, #fff3e8 8px, #ffe8d6 8px, #ffe8d6 16px)'
        : tone === 'extra'
          ? 'repeating-linear-gradient(135deg, #fff3e0, #fff3e0 8px, #ffe0b2 8px, #ffe0b2 16px)'
          : 'repeating-linear-gradient(135deg, #fafafa, #fafafa 8px, #f3f2f7 8px, #f3f2f7 16px)',
    }}
  >
    <Box
      component="span"
      sx={{
        fontSize: '0.75rem',
        color: tone === 'default' ? '#9b96ac' : WARN.ink,
        fontWeight: 600,
        backgroundColor: '#fff',
        border: `1px dashed ${tone === 'default' ? '#e8e5f0' : WARN.line}`,
        px: 1.2, py: 0.4, borderRadius: 999,
      }}
    >
      {label}
    </Box>
  </Box>
);

/** Bill-side loading cell — shown while Alfred is reading the invoice */
const BillLoadingCell: React.FC = () => (
  <Box
    sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
      px: 2, py: 1.6, borderBottom: '1px solid #f0eef6',
      backgroundColor: BILL_SIDE.headerBg,
    }}
  >
    <CircularProgress size={16} thickness={5} sx={{ color: FI_ORANGE }} />
    <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: FI_ORANGE }}>
      Importing…
    </Typography>
  </Box>
);

// ── Spine (match / action column) ─────────────────────────────────────────────

const Spine: React.FC<{
  kind: 'matched' | 'extra' | 'outstanding' | 'preview' | 'loading';
  deltaLabel: string;
  /** Optional micro-label under the $ delta (e.g. "ex GST") */
  deltaHint?: string;
  backgroundColor?: string;
  onAction?: () => void;
}> = ({ kind, deltaLabel, deltaHint, backgroundColor, onAction }) => {
  const badgeSx = {
    width: 36, height: 36, borderRadius: '50%',
    display: 'grid', placeItems: 'center', flexShrink: 0,
    '& svg': { width: 20, height: 20 },
  };
  // Outstanding uses WARN (orange) so unmatched rows scan as “needs attention”,
  // not the cooler slate used previously.
  const palette =
    kind === 'matched' || kind === 'preview' ? GO :
    kind === 'extra' || kind === 'outstanding' ? WARN :
    kind === 'loading' ? { main: FI_ORANGE, ink: FI_ORANGE, bg: '#fff3e0', line: '#ffcc80' } : SLATE;

  return (
    <Box
      sx={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5,
        borderLeft: '1px solid #f0eef6', borderRight: '1px solid #f0eef6',
        backgroundColor: backgroundColor ?? (kind === 'outstanding' || kind === 'extra' ? WARN.bg : '#fbfaff'),
        borderBottom: '1px solid #f0eef6',
        minHeight: 64,
      }}
    >
      {kind === 'extra' ? (
        <Button
          onClick={onAction}
          title="Add this invoice line to the PO"
          aria-label="Add to PO"
          sx={{
            ...badgeSx, minWidth: 0, p: 0,
            backgroundColor: FI_ORANGE,
            color: '#fff',
            border: `2px solid ${FI_ORANGE}`,
            boxShadow: '0 2px 8px rgba(245,124,0,0.45)',
            '&:hover': {
              backgroundColor: '#e65100',
              boxShadow: '0 3px 12px rgba(230,81,0,0.55)',
              transform: 'scale(1.06)',
            },
            transition: 'all 0.15s ease',
          }}
        >
          <AddIcon sx={{ fontSize: 22, fontWeight: 800 }} />
        </Button>
      ) : kind === 'loading' ? (
        <CircularProgress size={22} thickness={5} sx={{ color: FI_ORANGE }} />
      ) : (
        <Box sx={{
          ...badgeSx,
          width: 28, height: 28,
          backgroundColor: palette.bg, color: palette.main, border: `1px solid ${palette.line}`,
        }}>
          {kind === 'matched' || kind === 'preview' ? (
            <CheckIcon sx={{ fontSize: 16 }} />
          ) : (
            <ScheduleIcon sx={{ fontSize: 16 }} />
          )}
        </Box>
      )}
      {kind !== 'loading' && (
        <Typography sx={{
          fontWeight: 800, fontSize: kind === 'extra' || kind === 'outstanding' ? '0.78rem' : '0.72rem',
          color: palette.ink, fontVariantNumeric: 'tabular-nums', lineHeight: 1,
        }}>
          {deltaLabel}
        </Typography>
      )}
      {deltaHint && kind !== 'loading' && (
        <Typography sx={{ fontSize: '0.52rem', fontWeight: 700, color: palette.ink, letterSpacing: '0.04em', textTransform: 'uppercase', opacity: 0.85 }}>
          {deltaHint}
        </Typography>
      )}
      {kind === 'extra' && (
        <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: WARN.ink, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
          Add to PO
        </Typography>
      )}
    </Box>
  );
};

// ── Row block ─────────────────────────────────────────────────────────────────

type PoFieldUpdater = (
  inventoryCode: string,
  field: 'orderedQty' | 'receivedToDate',
  value: number,
) => void;

function matchedRow(
  pair: MatchedPair,
  gstMode: 'ex' | 'inc',
  onUpdatePoLineField?: PoFieldUpdater,
  onUpdateBillQty?: (billLineId: number, qty: number) => void,
) {
  const { billLine, poLine, lineDeltaEx, previewAdd } = pair;
  const billAmt = mulGst(billLine.lineTotal, gstMode);
  const billUnit = mulGst(billLine.unitPrice, gstMode);
  const poAmt = previewAdd || !poLine ? billAmt : mulGst(poLineEx(poLine), gstMode);
  const poUnit = previewAdd || !poLine ? billUnit : mulGst(poLine.unitPrice, gstMode);
  const delta = previewAdd ? 0 : mulGst(lineDeltaEx, gstMode);
  const deltaLabel = Math.abs(delta) < 0.005 ? '$0.00' : `${delta >= 0 ? '+' : '−'}${fmtMoney(Math.abs(delta))}`;
  const poCode = previewAdd || !poLine
    ? (billLine.internalCode || billLine.supplierCode)
    : (poLine.inventoryCode || poLine.supplierCode || '—');

  return (
    <Box key={`m-${billLine.id}`} sx={{ mx: 1, borderLeft: '1px solid #e8e5f0', borderRight: '1px solid #e8e5f0' }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: `1fr ${SPINE_W}px 1fr` } }}>
        {previewAdd || !poLine ? (
          <PoItemCell
            code={billLine.internalCode || billLine.supplierCode}
            desc={billLine.description}
            orderedQty={billLine.qty}
            receivedQty={billLine.qty}
            unitPrice={fmtMoney(poUnit)}
            amount={fmtMoney(poAmt)}
          />
        ) : (
          <PoItemCell
            code={poLine.inventoryCode || poLine.supplierCode || '—'}
            desc={poLine.description}
            orderedQty={poLine.orderedQty}
            receivedQty={poLine.receivedToDate}
            unitPrice={fmtMoney(poUnit)}
            amount={fmtMoney(poAmt)}
            onUpdate={onUpdatePoLineField
              ? (field, q) => onUpdatePoLineField(poLine.inventoryCode || poLine.supplierCode || poCode, field, q)
              : undefined}
          />
        )}
        <Spine kind={previewAdd ? 'preview' : 'matched'} deltaLabel={deltaLabel} />
        <BillItemCell
          code={billLine.internalCode || billLine.supplierCode}
          desc={billLine.description}
          sup={billLine.supplierCode}
          qty={billLine.qty}
          unitPrice={fmtMoney(billUnit)}
          amount={fmtMoney(billAmt)}
          onQtyChange={onUpdateBillQty ? (q) => onUpdateBillQty(billLine.id, q) : undefined}
        />
      </Box>
    </Box>
  );
}

function extraRow(bl: BillLineItem, gstMode: 'ex' | 'inc', onAccept: () => void) {
  const lineAmt = mulGst(bl.lineTotal, gstMode);
  // Spine delta always Ex GST so it doesn’t flip with the Inc/Ex toggle.
  const deltaEx = bl.lineTotal;
  return (
    <Box
      key={`e-${bl.id}`}
      sx={{
        mx: 1,
        borderLeft: `2px solid ${WARN.main}`,
        borderRight: `1px solid ${WARN.line}`,
        backgroundColor: WARN.bg,
      }}
    >
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: `1fr ${SPINE_W}px 1fr` } }}>
        <GhostCell label="Not on this PO" tone="extra" />
        <Spine
          kind="extra"
          deltaLabel={`+${fmtMoney(deltaEx)}`}
          deltaHint="ex GST"
          backgroundColor={WARN.bg}
          onAction={onAccept}
        />
        <BillItemCell
          code={bl.internalCode || bl.supplierCode}
          desc={bl.description}
          sup={bl.supplierCode}
          qty={bl.qty}
          unitPrice={fmtMoney(mulGst(bl.unitPrice, gstMode))}
          amount={fmtMoney(lineAmt)}
          backgroundColor={WARN.bg}
        />
      </Box>
    </Box>
  );
}

/** Soft peach full-row tint — unmatched PO lines must contrast with matched green rows. */
const OUTSTANDING_ROW_BG = '#fff0e8';

function outstandingRow(
  pl: POLineDetail,
  gstMode: 'ex' | 'inc',
  processing: boolean,
  onUpdatePoLineField?: PoFieldUpdater,
) {
  const lineEx = poLineEx(pl);
  // LINE / Unit columns follow Ex/Inc toggle; spine delta stays Ex GST always.
  const lineAmt = mulGst(lineEx, gstMode);
  const unitAmt = mulGst(pl.unitPrice, gstMode);
  return (
    <Box
      key={`o-${pl.inventoryCode}-${pl.description}`}
      sx={{
        mx: 1,
        borderLeft: `2px solid ${WARN.main}`,
        borderRight: `1px solid ${WARN.line}`,
        backgroundColor: OUTSTANDING_ROW_BG,
      }}
    >
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: `1fr ${SPINE_W}px 1fr` } }}>
        <PoItemCell
          code={pl.inventoryCode || pl.supplierCode || '—'}
          desc={pl.description}
          orderedQty={pl.orderedQty}
          receivedQty={pl.receivedToDate}
          unitPrice={fmtMoney(unitAmt)}
          amount={fmtMoney(lineAmt)}
          backgroundColor={OUTSTANDING_ROW_BG}
          onUpdate={onUpdatePoLineField
            ? (field, q) => onUpdatePoLineField(pl.inventoryCode || pl.supplierCode || '', field, q)
            : undefined}
        />
        <Spine
          kind={processing ? 'loading' : 'outstanding'}
          deltaLabel={`−${fmtMoney(lineEx)}`}
          deltaHint={processing ? undefined : 'ex GST'}
          backgroundColor={OUTSTANDING_ROW_BG}
        />
        {processing ? <BillLoadingCell /> : <GhostCell label="Not on this invoice" tone="outstanding" />}
      </Box>
    </Box>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export const BillReconcileSideBySide: React.FC<BillReconcileSideBySideProps> = ({
  linkedPo,
  bill,
  billImported,
  processing = false,
  lineItems,
  poLinesForRecon,
  billMarkup,
  onBillMarkupChange,
  gstMode,
  onGstModeChange,
  acceptedExtras,
  onAcceptExtra,
  onAcceptAllExtras,
  onUpdatePoLineField,
  onReceiveAllMatched,
  onUpdateBillQty,
  supplier,
}) => {
  const bands = buildReconcileBands(billImported ? lineItems : [], poLinesForRecon, acceptedExtras);
  const totals = computeSideTotals(billImported ? lineItems : [], poLinesForRecon);

  // Show outstanding rows always (the unmatched PO lines) — just no band heading.
  const unmatchedReceived = bands.matched.filter(
    p => p.poLine && !p.previewAdd && p.poLine.receivedToDate < p.billLine.qty,
  ).length;

  // Compact Difference colour: green = fully matched, blue = awaiting later invoice, amber = needs review.
  const matchedVarianceEx = bands.matched.reduce((s, p) => s + p.lineDeltaEx, 0);
  const invoicedLinesOk = billImported && !processing
    && Math.abs(matchedVarianceEx) <= 0.05 && bands.extras.length === 0;
  const awaitingOnly = invoicedLinesOk && bands.outstanding.length > 0;
  const fullyMatched = invoicedLinesOk && bands.outstanding.length === 0;
  const diffColor = fullyMatched ? GO.ink : awaitingOnly ? SLATE.ink : WARN.ink;

  const poDisplay = (ex: number) => fmtMoney(mulGst(ex, gstMode));
  // Difference = bill − PO on ex-GST subtotals (sum of the Match column), not Inc totals.
  const diffLabel = totals.differenceEx >= 0
    ? `+${fmtMoney(totals.differenceEx)}`
    : `−${fmtMoney(Math.abs(totals.differenceEx))}`;

  const gridCols = { xs: '1fr', md: `1fr ${SPINE_W}px 1fr` } as const;

  return (
    <Box sx={{ mb: 2 }} className="no-print">
      {/* Toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1.5, px: 0.5 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1b1726' }}>
          Ordered vs invoiced <Typography component="span" sx={{ color: INK_MUTE, fontWeight: 500 }}>· side by side</Typography>
        </Typography>
        <Box sx={{ flex: 1 }} />
        {billImported && (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, border: '1px solid #e8e5f0', borderRadius: '10px', px: 1.2, py: 0.6, backgroundColor: '#fff' }}>
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#9b96ac' }}>Markup</Typography>
            <TextField
              value={billMarkup}
              onChange={e => onBillMarkupChange(e.target.value)}
              size="small"
              variant="outlined"
              inputProps={{ style: { width: 36, textAlign: 'right', fontWeight: 700, padding: '4px 6px', fontSize: '0.85rem', color: FI_ORANGE } }}
              sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#fff8f0' } }}
            />
            <Typography sx={{ color: '#9b96ac', fontWeight: 700, fontSize: '0.8rem' }}>%</Typography>
            {supplier && (
              <Typography sx={{ fontSize: '0.58rem', color: '#bbb', ml: 0.5 }}>{supplier.code} default: {supplier.defaultMarkup}%</Typography>
            )}
          </Box>
        )}
        {billImported && (
          <ToggleButtonGroup
            value={gstMode}
            exclusive
            size="small"
            onChange={(_, v) => { if (v) onGstModeChange(v); }}
            sx={{
              border: '1px solid #e8e5f0', borderRadius: '9px', overflow: 'hidden', backgroundColor: '#f5f4f9',
              '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', px: 1.5, py: 0.6, border: 0 },
              '& .Mui-selected': { backgroundColor: '#fff !important', color: '#1b1726', boxShadow: '0 1px 2px rgba(0,0,0,.06)' },
            }}
          >
            <ToggleButton value="ex">Ex GST</ToggleButton>
            <ToggleButton value="inc">Inc GST</ToggleButton>
          </ToggleButtonGroup>
        )}
      </Box>

      <Box sx={{ px: 1, pb: 0.5 }}>
        {/* Panel headers — colour-coded identity: purple PO · orange Bill */}
        <Box sx={{ display: 'grid', gridTemplateColumns: gridCols, mx: 1, border: '1px solid #e8e5f0', borderBottom: 0, borderRadius: '12px 12px 0 0', overflow: 'hidden' }}>
          {/* Purchase Order side */}
          <Box sx={{ px: 2, py: 1.2, backgroundColor: PO_SIDE.headerBg, borderTop: `3px solid ${PO_SIDE.accent}`, display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <ShoppingCartOutlinedIcon sx={{ fontSize: 20, color: PO_SIDE.accent, flexShrink: 0 }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, letterSpacing: '0.09em', textTransform: 'uppercase', color: PO_SIDE.accent, lineHeight: 1.1 }}>
                {PO_SIDE.label}
              </Typography>
              <Typography sx={{ fontWeight: 800, fontSize: '0.86rem', color: PO_SIDE.accent, lineHeight: 1.2 }}>{linkedPo.id}</Typography>
            </Box>
            <Box component="span" sx={{ ml: 'auto', fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: PO_SIDE.accent, backgroundColor: '#fff', border: `1px solid ${PO_SIDE.accent}40`, borderRadius: 999, px: 1, py: 0.3 }}>
              Ordered
            </Box>
          </Box>
          {/* Match spine */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', justifyContent: 'center', backgroundColor: '#fbfaff', borderTop: '3px solid #d8d3e8', borderLeft: '1px solid #e8e5f0', borderRight: '1px solid #e8e5f0' }}>
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#9b96ac' }}>Match</Typography>
          </Box>
          {/* Supplier Bill side */}
          <Box sx={{ px: 2, py: 1.2, backgroundColor: BILL_SIDE.headerBg, borderTop: `3px solid ${BILL_SIDE.accent}`, display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <ReceiptLongOutlinedIcon sx={{ fontSize: 20, color: BILL_SIDE.accent, flexShrink: 0 }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, letterSpacing: '0.09em', textTransform: 'uppercase', color: BILL_SIDE.accent, lineHeight: 1.1 }}>
                {processing ? 'Bill being imported' : BILL_SIDE.label}
              </Typography>
              <Typography sx={{ fontWeight: 800, fontSize: '0.86rem', color: BILL_SIDE.accent, lineHeight: 1.2 }}>{bill.billNo}</Typography>
            </Box>
            {processing ? (
              <CircularProgress size={16} thickness={5} sx={{ ml: 'auto', color: FI_ORANGE }} />
            ) : (
              <Box component="span" sx={{ ml: 'auto', fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: BILL_SIDE.accent, backgroundColor: '#fff', border: `1px solid ${BILL_SIDE.accent}40`, borderRadius: 999, px: 1, py: 0.3 }}>
                Invoiced
              </Box>
            )}
          </Box>
        </Box>

        {/* Column sub-headers — Unit + Line on both sides for price compare */}
        <Box sx={{ display: { xs: 'none', md: 'grid' }, gridTemplateColumns: gridCols, mx: 1, borderLeft: '1px solid #e8e5f0', borderRight: '1px solid #e8e5f0', backgroundColor: '#fff' }}>
          <Box
            sx={{
              display: 'grid', gridTemplateColumns: PO_QTY_GRID,
              gap: 0.5, px: 1.2, py: 0.8,
            }}
          >
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#9b96ac' }}>Item</Typography>
            <Typography sx={{ fontSize: '0.58rem', fontWeight: 800, letterSpacing: '0.03em', textTransform: 'uppercase', color: BRAND, textAlign: 'right' }} title="Ordered quantity">Qty</Typography>
            <Typography sx={{ fontSize: '0.58rem', fontWeight: 800, letterSpacing: '0.03em', textTransform: 'uppercase', color: BRAND, textAlign: 'right' }} title="Received quantity">Rcvd</Typography>
            <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, letterSpacing: '0.03em', textTransform: 'uppercase', color: '#9b96ac', textAlign: 'right' }} title={`Unit price (${gstMode})`}>Unit</Typography>
            <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, letterSpacing: '0.03em', textTransform: 'uppercase', color: '#9b96ac', textAlign: 'right' }} title={`Line total (${gstMode})`}>Line</Typography>
          </Box>
          <Box sx={{ borderLeft: '1px solid #f0eef6', borderRight: '1px solid #f0eef6' }} />
          <Box
            sx={{
              display: 'grid', gridTemplateColumns: BILL_QTY_GRID,
              gap: 0.6, px: 1.5, py: 0.8,
            }}
          >
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#9b96ac' }}>Item</Typography>
            <Typography sx={{ fontSize: '0.58rem', fontWeight: 800, letterSpacing: '0.03em', textTransform: 'uppercase', color: FI_ORANGE, textAlign: 'right' }} title="Bill / invoiced quantity">Qty</Typography>
            <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, letterSpacing: '0.03em', textTransform: 'uppercase', color: '#9b96ac', textAlign: 'right' }} title={`Unit price (${gstMode})`}>Unit</Typography>
            <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, letterSpacing: '0.03em', textTransform: 'uppercase', color: '#9b96ac', textAlign: 'right' }} title={`Line total (${gstMode})`}>Line</Typography>
          </Box>
        </Box>

        {/* Bands + rows */}
        {!billImported && !processing && bands.outstanding.length === 0 && (
          <Box sx={{ mx: 1, p: 4, textAlign: 'center', border: '1px solid #e8e5f0', borderTop: '1px solid #f0eef6' }}>
            <Typography sx={{ fontSize: '0.85rem', color: INK_MUTE }}>Import a supplier invoice to compare against this PO</Typography>
          </Box>
        )}

        {processing && !billImported && bands.outstanding.length === 0 && (
          <Box sx={{
            mx: 1, p: 4, textAlign: 'center', border: '1px solid #e8e5f0', borderTop: '1px solid #f0eef6',
            backgroundColor: BILL_SIDE.headerBg, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.2,
          }}>
            <CircularProgress size={28} thickness={4} sx={{ color: FI_ORANGE }} />
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: FI_ORANGE }}>
              Alfred is reading your invoice…
            </Typography>
            <Typography sx={{ fontSize: '0.72rem', color: INK_MUTE }}>
              Line items will appear here when parsing finishes
            </Typography>
          </Box>
        )}

        {bands.matched.length > 0 && (
          <>
            {/* PO receipt action — top of matcher, left-aligned under the PO column */}
            {unmatchedReceived > 0 && onReceiveAllMatched && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: `1fr ${SPINE_W}px 1fr` },
                  mx: 1,
                  borderLeft: '1px solid #e8e5f0', borderRight: '1px solid #e8e5f0',
                  borderTop: '1px solid #f0eef6', backgroundColor: GO.bg,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75 }}>
                  <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: BRAND }}>
                    PO
                  </Typography>
                  <Button
                    onClick={onReceiveAllMatched}
                    sx={{
                      textTransform: 'none', fontWeight: 700, fontSize: '0.75rem',
                      border: `2px solid ${GO.main}`, color: '#fff', backgroundColor: GO.main,
                      borderRadius: '8px', px: 1.4, py: 0.55,
                      boxShadow: '0 2px 6px rgba(15,157,99,0.28)',
                      '&:hover': { backgroundColor: GO.ink },
                    }}
                  >
                    Receive all {unmatchedReceived} on PO →
                  </Button>
                </Box>
                <Box sx={{ borderLeft: '1px solid #f0eef6', borderRight: '1px solid #f0eef6' }} />
                <Box />
              </Box>
            )}
            {bands.matched.map(p => matchedRow(p, gstMode, onUpdatePoLineField, onUpdateBillQty))}
          </>
        )}

        {bands.extras.length > 0 && (
          <>
            <Box
              sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'flex-start',
                mx: 1, px: 1.5, py: 0.7,
                borderLeft: '1px solid #e8e5f0', borderRight: '1px solid #e8e5f0',
                borderTop: '1px solid #f0eef6', backgroundColor: WARN.bg,
              }}
            >
              <Button
                onClick={onAcceptAllExtras}
                sx={{
                  textTransform: 'none', fontWeight: 700, fontSize: '0.75rem',
                  border: `2px solid ${FI_ORANGE}`, color: '#fff', backgroundColor: FI_ORANGE,
                  borderRadius: '8px', px: 1.4, py: 0.55,
                  boxShadow: '0 2px 6px rgba(245,124,0,0.35)',
                  '&:hover': { backgroundColor: '#e65100' },
                }}
              >
                Add all {bands.extras.length} to PO →
              </Button>
            </Box>
            {bands.extras.map(bl => extraRow(bl, gstMode, () => onAcceptExtra(bl.id)))}
          </>
        )}

        {/* Outstanding / unmatched PO lines — rows only, no heading */}
        {bands.outstanding.map(pl => outstandingRow(pl, gstMode, processing, onUpdatePoLineField))}

        {/* Close bottom border on last row */}
        {(bands.matched.length + bands.extras.length + bands.outstanding.length) > 0 && (
          <Box sx={{ mx: 1, borderBottom: '1px solid #e8e5f0', borderRadius: '0 0 12px 12px' }} />
        )}

        {/* Footer totals */}
        {(billImported || processing || bands.outstanding.length > 0) && (
          <Box sx={{ display: 'grid', gridTemplateColumns: gridCols, mx: 1, mt: 1.8, gap: { xs: 1, md: 0 } }}>
            <Box sx={{ p: 1.8, borderRadius: '12px', backgroundColor: BRAND_LIGHT, border: `1px solid ${BRAND}2e` }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: INK_MUTE, py: 0.25 }}>
                <span>Ordered subtotal ({gstMode})</span>
                <span className="num" style={{ fontWeight: 600, color: '#3a3550' }}>{poDisplay(totals.poSubEx)}</span>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: INK_MUTE, py: 0.25 }}>
                <span>GST 10%</span>
                <span className="num" style={{ fontWeight: 600, color: '#3a3550' }}>{poDisplay(totals.poGst)}</span>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mt: 0.8, pt: 1, borderTop: `1px solid ${BRAND}2e` }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: BRAND }}>PO total</Typography>
                <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: BRAND, fontVariantNumeric: 'tabular-nums' }}>
                  {fmtMoney(totals.poTotalInc)}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', py: { xs: 1, md: 0 } }}>
              <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#9b96ac' }}>Difference</Typography>
              <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: diffColor, mt: 0.3, fontVariantNumeric: 'tabular-nums' }}>
                {processing && !billImported ? '—' : diffLabel}
              </Typography>
              {!(processing && !billImported) && (
                <Typography sx={{ fontSize: '0.52rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#9b96ac', mt: 0.15 }}>
                  ex GST
                </Typography>
              )}
              {awaitingOnly && (
                <Typography sx={{ fontSize: '0.56rem', fontWeight: 700, color: SLATE.ink, mt: 0.2, lineHeight: 1.25 }}>
                  awaiting<br />invoice
                </Typography>
              )}
              {fullyMatched && <CheckIcon sx={{ fontSize: 15, color: GO.main, mt: 0.2 }} />}
            </Box>

            <Box sx={{ p: 1.8, borderRadius: '12px', backgroundColor: BILL_SIDE.headerBg, border: `1px solid ${FI_ORANGE}3a` }}>
              {billImported ? (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: INK_MUTE, py: 0.25 }}>
                    <span>Invoiced subtotal ({gstMode})</span>
                    <span style={{ fontWeight: 600, color: '#3a3550' }}>{poDisplay(totals.billSubEx)}</span>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: INK_MUTE, py: 0.25 }}>
                    <span>GST 10%</span>
                    <span style={{ fontWeight: 600, color: '#3a3550' }}>{poDisplay(totals.billGst)}</span>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mt: 0.8, pt: 1, borderTop: `1px solid ${FI_ORANGE}3a` }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: FI_ORANGE }}>Bill total</Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: FI_ORANGE, fontVariantNumeric: 'tabular-nums' }}>
                      {fmtMoney(totals.billTotalInc)}
                    </Typography>
                  </Box>
                </>
              ) : processing ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 80, gap: 1 }}>
                  <CircularProgress size={22} thickness={5} sx={{ color: FI_ORANGE }} />
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: FI_ORANGE }}>
                    Bill being imported…
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 80 }}>
                  <Typography sx={{ fontSize: '0.78rem', color: '#9b96ac', fontStyle: 'italic' }}>Awaiting invoice import</Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}

      </Box>
    </Box>
  );
};

export default BillReconcileSideBySide;
